import type { ReactNode } from "react";

import {
  SITE_URL,
  baseCrumbs,
  sharedRelated,
  orgNode,
  faqJsonld,
  breadcrumbJsonld,
  webPageNode,
  type LearnFAQ,
} from "./learnShared";
import type { LearnPageData } from "./learnShared";

const P = (s: ReactNode): ReactNode => <p>{s}</p>;

const faqs: LearnFAQ[] = [
  {
    question: "Where can I get 5-day TMS treatment?",
    answer:
      "SAINT® — the FDA-cleared five-day TMS protocol — is available at Neuro Wellness TMS Centers of America in Coral Springs, Florida. Not every clinic offering 'accelerated TMS' offers SAINT®. Ask whether they use the Magnus Neuromodulation System with fMRI targeting, and whether the protocol is FDA-cleared.",
  },
  {
    question: "Is all accelerated TMS the same as SAINT®?",
    answer:
      "No. SAINT® is one specific FDA-cleared protocol: ten iTBS sessions per day for five days with fMRI targeting on the Magnus system. Other accelerated schedules use conventional devices without fMRI targeting — these are off-label and have different evidence. Protocol names matter.",
  },
  {
    question: "Why do protocol names matter?",
    answer:
      "Different accelerated protocols use different devices, targeting methods, session counts, and evidence. SAINT® has its own FDA clearance and fMRI targeting. A clinic offering 'accelerated TMS' on a conventional device may be delivering an off-label schedule. Ask specifically which protocol is being offered.",
  },
  {
    question: "What targeting methods do accelerated protocols use?",
    answer:
      "SAINT® uses fMRI to compute an individualized target. Other accelerated protocols may use scalp landmarks, the '5.5 cm rule,' or motor-threshold-based positioning — the same methods as conventional TMS, just delivered more frequently. fMRI targeting is a key differentiator of SAINT®.",
  },
  {
    question: "What questions should I ask a provider offering 5-day TMS?",
    answer:
      "Ask: Which protocol is this — SAINT® or another? Do you use the Magnus Neuromodulation System? Is the target selected from my own fMRI? Is the protocol FDA-cleared or off-label? Who oversees care? What is the cost and insurance process?",
  },
];

export const page5DayTms: LearnPageData = {
  slug: "5-day-tms-treatment",
  title: "Where Can I Get 5-Day TMS Treatment? | Neuro Wellness",
  description:
    "5-day TMS treatment: not every accelerated TMS is SAINT®. Different protocols, why names matter, treatment frequency, targeting methods, and questions to ask providers.",
  canonical: "/learn/5-day-tms-treatment",
  h1: "Where Can I Get 5-Day TMS Treatment?",
  breadcrumbLabel: "5-Day TMS Treatment",
  crumbs: baseCrumbs("5-Day TMS Treatment"),
  shortAnswer:
    "Not every 'accelerated TMS' program is SAINT®. SAINT® (Stanford Accelerated Intelligent Neuromodulation Therapy) is one specific FDA-cleared protocol: ten intermittent theta burst sessions per day for five consecutive days, with fMRI-guided targeting, on the Magnus Neuromodulation System. Other accelerated schedules use conventional devices without fMRI targeting — these are off-label and have different evidence. Neuro Wellness TMS Centers of America in Coral Springs, Florida, offers the FDA-cleared SAINT® protocol. This page explains the differences between accelerated protocols, why protocol names matter, treatment frequency, targeting methods, and questions to ask providers.",
  keyTakeaways: [
    "Not every 'accelerated TMS' is SAINT®.",
    "SAINT® is FDA-cleared with fMRI targeting on the Magnus system.",
    "Other accelerated schedules are off-label.",
    "Protocol names matter — ask which one is offered.",
    "fMRI targeting is a key differentiator.",
  ],

  sections: [
    {
      heading: "Not Every Accelerated TMS Program Is SAINT®",
      body: (
        <>
          {P(
            "'Accelerated TMS' is a broad term for any TMS schedule with more than one session per day. SAINT® is one specific accelerated protocol with its own FDA clearance (510(k) K220177), fMRI targeting, and the Magnus Neuromodulation System. Other accelerated schedules use conventional devices — such as NeuroStar or BrainsWay — without fMRI targeting, and are off-label.",
          )}
          {P(
            <>
              Learn more about{" "}
              <a
                href="/learn/what-is-saint-tms"
                className="font-medium text-accent underline underline-offset-2 hover:opacity-80"
              >
                what SAINT® TMS is
              </a>
              .
            </>,
          )}
        </>
      ),
    },
    {
      heading: "Different Accelerated Protocols",
      body: (
        <>
          {P(
            "Accelerated protocols vary widely. Some deliver two sessions per day; others deliver ten. Some use theta burst stimulation; others use 10 Hz. Some use fMRI targeting; others use scalp landmarks. Only SAINT® has its own FDA clearance as an accelerated protocol. Other schedules are off-label uses of FDA-cleared devices.",
          )}
          {P(
            <>
              Learn more about{" "}
              <a
                href="/learn/accelerated-tms-theta-burst-stimulation"
                className="font-medium text-accent underline underline-offset-2 hover:opacity-80"
              >
                accelerated TMS and theta burst
              </a>
              .
            </>,
          )}
        </>
      ),
    },
    {
      heading: "Why Protocol Names Matter",
      body: (
        <>
          {P(
            "Protocol names matter because they indicate different devices, targeting methods, session counts, and evidence. A clinic offering 'accelerated TMS' on a conventional device may be delivering an off-label schedule with less evidence than SAINT®. Ask specifically which protocol is being offered, what device is used, and whether it is FDA-cleared or off-label.",
          )}
        </>
      ),
    },
    {
      heading: "Treatment Frequency",
      body: (
        <>
          {P(
            "SAINT® delivers ten sessions per day for five days — 50 sessions total. Other accelerated protocols may deliver fewer sessions per day over a longer period. Treatment frequency affects the total course and the daily time commitment. SAINT®'s ten-per-day schedule with 50-minute breaks means full days at the clinic for one week.",
          )}
        </>
      ),
    },
    {
      heading: "Targeting Methods",
      body: (
        <>
          {P(
            "SAINT® uses fMRI to compute an individualized target from your brain's functional connectivity. Other accelerated protocols may use scalp landmarks, the '5.5 cm rule,' or motor-threshold-based positioning — the same methods as conventional TMS, just delivered more frequently. fMRI targeting is a key differentiator of SAINT®.",
          )}
          {P(
            <>
              Learn more about{" "}
              <a
                href="/learn/fmri-guided-tms-for-depression"
                className="font-medium text-accent underline underline-offset-2 hover:opacity-80"
              >
                fMRI-guided TMS
              </a>
              .
            </>,
          )}
        </>
      ),
    },
    {
      heading: "Questions to Ask Providers",
      body: (
        <>
          <ul className="mt-4 space-y-2">
            <li className="relative pl-5 leading-relaxed text-foreground before:absolute before:left-0 before:top-2.5 before:h-1.5 before:w-1.5 before:rounded-full before:bg-accent">
              Which protocol is this — SAINT® or another?
            </li>
            <li className="relative pl-5 leading-relaxed text-foreground before:absolute before:left-0 before:top-2.5 before:h-1.5 before:w-1.5 before:rounded-full before:bg-accent">
              Do you use the Magnus Neuromodulation System?
            </li>
            <li className="relative pl-5 leading-relaxed text-foreground before:absolute before:left-0 before:top-2.5 before:h-1.5 before:w-1.5 before:rounded-full before:bg-accent">
              Is the target selected from my own fMRI?
            </li>
            <li className="relative pl-5 leading-relaxed text-foreground before:absolute before:left-0 before:top-2.5 before:h-1.5 before:w-1.5 before:rounded-full before:bg-accent">
              Is the protocol FDA-cleared or off-label?
            </li>
            <li className="relative pl-5 leading-relaxed text-foreground before:absolute before:left-0 before:top-2.5 before:h-1.5 before:w-1.5 before:rounded-full before:bg-accent">
              Who oversees care — a psychiatrist?
            </li>
            <li className="relative pl-5 leading-relaxed text-foreground before:absolute before:left-0 before:top-2.5 before:h-1.5 before:w-1.5 before:rounded-full before:bg-accent">
              What is the cost and insurance process?
            </li>
          </ul>
        </>
      ),
    },
  ],

  faqs,
  sources: [
    {
      citation:
        "U.S. Food and Drug Administration. 510(k) Premarket Notification — Magnus Neuromodulation System (K220177, September 2022).",
      url: "https://www.accessdata.fda.gov/scripts/cdrh/cfdocs/cfpmn/pmn.cfm",
    },
  ],

  reviewBlock: {
    writtenBy: "Neuro Wellness TMS Centers of America",
    reviewedBy: "Daniel Pietras, MD",
    published: "November 3, 2026",
    lastReviewed: "November 3, 2026",
    lastUpdated: "November 3, 2026",
  },
  schemaJson: {
    "@context": "https://schema.org",
    "@graph": [
      webPageNode({
        id: `${SITE_URL}/learn/5-day-tms-treatment#webpage`,
        name: "Where Can I Get 5-Day TMS Treatment?",
        url: `${SITE_URL}/learn/5-day-tms-treatment`,
        description:
          "5-day TMS treatment: not every accelerated TMS is SAINT®. Protocols, targeting, frequency, and questions to ask.",
      }),
      orgNode,
      faqJsonld(faqs, `${SITE_URL}/learn/5-day-tms-treatment#faq`),
      breadcrumbJsonld(baseCrumbs("5-Day TMS Treatment")),
    ],
  },
  relatedLinks: [
    ...sharedRelated,
    {
      title: "What Is SAINT® TMS?",
      href: "/learn/what-is-saint-tms",
      description: "Protocol, FDA clearance, and evidence",
    },
    {
      title: "Where Can I Get SAINT® TMS?",
      href: "/learn/where-can-i-get-saint-tms",
      description: "Finding and verifying a provider",
    },
  ],
};
